<script lang="ts" setup>
import { useRoute } from 'vue-router';
import { GuidedShowcaseLayout, ActionBarLayout } from '@Shared';
import useFlowNavigation from '@Use/useFlowNavigation';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { trackClientKbaPrimed } from '@Analytics/kba.mixpanel';
import SecuredDataGraphic from '@Assets/images/secured-data.svg';
import ShowcaseImage from '@Components/ShowcaseImage.vue';

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const { advanceFlow } = useFlowNavigation();
const route = useRoute();
const context = (route.query.context as string) ?? '';

const handleContinue = () => {
    trackClientKbaPrimed(context);
    advanceFlow();
};
</script>

<template>
    <guided-showcase-layout
        suppress-back-to-dashboard
        title="Verify your information"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">

        <app-form-content-layout>
            <p>
                Next, we'll ask for a few details to create the security questions that will help us verify your identity.
            </p>

            <app-highlight-banner class="privacy-banner">
                <div class="privacy-banner__content">
                    <img :src="SecuredDataGraphic" alt="" class="privacy-banner__icon" />
                    <p>
                        This information will just be used to create questions only you can answer. It will not be stored by CertifID.
                        <a href="https://certifidhelpcenter.zendesk.com/hc/en-us/articles/18426417319579-I-don-t-feel-comfortable-providing-my-personal-information" target="_blank" rel="noopener noreferrer">More information</a>
                    </p>
                </div>
            </app-highlight-banner>
        </app-form-content-layout>

        <action-bar-layout>
            <app-button
                size="large"
                @onClick="handleContinue"
                data-id="kba-primer-continue">
                Continue
            </app-button>
        </action-bar-layout>

        <template #branded>
            <showcase-image />
        </template>
    </guided-showcase-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.privacy-banner {
    margin-bottom: 1rem;

    &__content {
        display: flex;
        gap: 0.625rem;
        align-items: flex-start;
    }

    &__icon {
        flex-shrink: 0;
        width: 3rem;
        height: auto;
    }
}
</style>
